import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import projectService from '../services/projectService';
import { useAuth } from './AuthContext';
import { debugLog, debugWarn } from '../utils/debugLogger';

const ProjectContext = createContext();

export const ProjectProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [error, setError] = useState('');

  const loadProjects = useCallback(async () => {
    if (!isAuthenticated || !user) {
      debugWarn('No authenticated user, skipping project load');
      setLoading(false);
      setProjects([]);
      setUserRole(null);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await projectService.getProjects();

      debugLog('Projects loaded from context:', response.projects?.length || 0);
      debugLog('User role from projects response:', response.userRole);

      setProjects(response.projects || []);
      setUserRole(response.userRole || null);
    } catch (err) {
      console.error('Error loading projects in context:', err);

      if (err.response?.status === 401) {
        debugWarn('Unauthenticated user (401), ignoring project load error');
        setProjects([]);
        setUserRole(null);
      } else {
        setError(err.message || 'Failed to load projects');
        setProjects([]);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      debugLog('Authenticated user detected, loading projects...');
      loadProjects();
      return;
    }

    debugLog('Waiting for authenticated user before loading projects...');
    setProjects([]);
    setUserRole(null);
    setLoading(false);
  }, [isAuthenticated, user, loadProjects]);

  const refreshProjects = useCallback(() => {
    if (isAuthenticated && user) {
      debugLog('Refreshing projects from context...');
      loadProjects();
      return;
    }

    debugWarn('Cannot refresh projects without an authenticated user');
  }, [isAuthenticated, user, loadProjects]);

  const addProject = (newProject) => {
    debugLog('Project added to context:', newProject?.name);
    setProjects((prev) => [newProject, ...prev]);
  };

  const updateProjectInContext = (updatedProject) => {
    debugLog('Project updated in context:', updatedProject?.name);
    setProjects((prev) => (
      prev.map((project) => (project.id === updatedProject.id ? updatedProject : project))
    ));
  };

  const removeProject = (projectId) => {
    debugLog('Project removed from context:', projectId);
    setProjects((prev) => prev.filter((project) => project.id !== projectId));
  };

  const value = {
    projects,
    loading,
    userRole,
    error,
    refreshProjects,
    addProject,
    updateProject: updateProjectInContext,
    removeProject,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used inside ProjectProvider');
  }
  return context;
};
